// src/components/workspace/LessonWorkspace.tsx
//
// Where a lesson meets the in-app workspace:
//
//   WorkspaceCard   on the Do step: "Do it here" with the project this
//                   lesson carries on (the one used last), a way to switch
//                   or start a new one, and the button that opens it
//   ProjectAttach   on the Hand in step: the project goes in with the work

import { useEffect, useMemo, useState } from "react";
import { Atom, Blocks, Code2, Laptop, Plus, Smartphone } from "lucide-react";
import { useCreateProjectMutation, useGetProjectsQuery } from "../../app/api/workspaceApi";
import type { ProjectSummary, WorkspaceKind } from "../../app/api/workspaceApi";
import { errorText } from "../../app/api/learningApi";

export const WORKSPACE_TEXT: Record<
  WorkspaceKind,
  { open: string; doHere: string; newProject: string; elsewhere: string; icon: typeof Blocks; stepsHint: string }
> = {
  scratch: {
    open: "Open Scratch",
    doHere: "Do it here, in Scratch",
    newProject: "Start a new Scratch project",
    elsewhere: "Using Scratch on scratch.mit.edu or another computer? That works too: hand in a photo or video as usual.",
    icon: Blocks,
    stepsHint: "When a step says to open Scratch or go to scratch.mit.edu, use the Scratch here instead. No Scratch account is needed.",
  },
  web: {
    open: "Open the code editor",
    doHere: "Do it here, in the code editor",
    newProject: "Start a new website",
    elsewhere: "Working in VS Code on your computer? That works too: hand in what the lesson asks for as usual.",
    icon: Code2,
    stepsHint:
      "When a step says VS Code, use the editor here. When it says Chrome or Live Server, look at the preview; the Console is under it. Steps about the terminal, git or Netlify still happen on your computer.",
  },
  react: {
    open: "Open the code editor",
    doHere: "Do it here: your React app runs in the browser",
    newProject: "Start a new React app",
    elsewhere: "Building it with Vite and npm on your computer, as the lesson shows? That works too: hand in as usual. (Download takes your app to VS Code.)",
    icon: Atom,
    stepsHint:
      "In the editor here your app already runs: skip steps like npm create, npm install and npm run dev, and add packages to package.json instead. Git and deploying still happen on your computer.",
  },
  native: {
    open: "Open the code editor",
    doHere: "Do it here: your app runs in a phone preview",
    newProject: "Start a new React Native app",
    elsewhere: "The preview runs in the browser; camera, location and notifications need Expo Go on a phone, as the lesson shows. Download takes your app to your computer.",
    icon: Smartphone,
    stepsHint:
      "In the editor here your app already runs in the phone preview: skip the create-expo-app and npm steps. Steps that need a real phone (Expo Go, camera, location) still happen on the phone.",
  },
};

export const PROJECT_NOUN: Record<WorkspaceKind, string> = {
  scratch: "Scratch project",
  web: "code",
  react: "React app",
  native: "React Native app",
};

export function fmtAgo(iso: string) {
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const h = Math.round(mins / 60);
  if (h < 24) return `${h} hour${h > 1 ? "s" : ""} ago`;
  const d = Math.round(h / 24);
  if (d < 14) return `${d} day${d > 1 ? "s" : ""} ago`;
  return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

/** The project a lesson should open: the one last used on it, else the most recent. */
export function defaultProject(projects: ProjectSummary[], lessonKey: string) {
  return projects.find((p) => p.lastLessonKey === lessonKey) ?? projects[0] ?? null;
}

export function WorkspaceCard({
  childId,
  lessonKey,
  kind,
  chosen,
  onChoose,
  onOpen,
}: {
  childId: string;
  lessonKey: string;
  kind: WorkspaceKind;
  chosen: string | null;
  onChoose: (id: string) => void;
  onOpen: (id: string) => void;
}) {
  const t = WORKSPACE_TEXT[kind];
  const { data, isLoading } = useGetProjectsQuery({ childId, kind });
  const [create, { isLoading: creating }] = useCreateProjectMutation();
  const [picking, setPicking] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const projects = useMemo(() => data?.data ?? [], [data]);
  const current = projects.find((p) => p.id === chosen) ?? null;
  const smallScreen = kind === "scratch" && window.innerWidth < 900;

  // Choose the lesson's project once the list arrives
  useEffect(() => {
    if (!chosen && projects.length) {
      const d = defaultProject(projects, lessonKey);
      if (d) onChoose(d.id);
    }
  }, [projects]); // eslint-disable-line react-hooks/exhaustive-deps

  const startNew = async () => {
    setMsg(null);
    try {
      const { data: p } = await create({ childId, kind, lessonKey }).unwrap();
      onChoose(p.id);
      setPicking(false);
      onOpen(p.id);
    } catch (err) {
      setMsg(errorText(err, "Could not start a new project"));
    }
  };

  const Icon = t.icon;
  return (
    <div className="rounded-[22px] border-2 border-[#1b2d5e] bg-[#f6f8fc] p-5">
      <p className="flex items-center gap-2 text-[15px] font-bold text-[#16213e]">
        <Icon className="h-5 w-5 text-[#1b2d5e]" /> {t.doHere}
      </p>

      {isLoading ? (
        <div className="mt-3 h-12 animate-pulse rounded-2xl bg-[#ebeff7]" />
      ) : current && !picking ? (
        <p className="mt-2 text-[14px] text-[#3b4560]">
          Your project: <b className="text-[#16213e]">{current.name}</b>
          <span className="text-[#8189a0]"> · saved {fmtAgo(current.updatedAt)}</span>
          {projects.length > 0 && (
            <button type="button" onClick={() => setPicking(true)} className="ml-2 text-[13px] font-semibold text-[#1b2d5e] underline-offset-2 hover:underline">
              Change
            </button>
          )}
        </p>
      ) : projects.length > 0 ? (
        <div className="mt-3 space-y-1.5">
          <p className="text-[13px] font-semibold text-[#5f6780]">Which project?</p>
          {projects.slice(0, 8).map((p) => (
            <label key={p.id} className="flex cursor-pointer items-center gap-3 rounded-xl bg-white px-3 py-2 text-[14px] ring-1 ring-[#e5e8ef] hover:ring-[#cdd4e2]">
              <input
                type="radio"
                name={`project-${lessonKey}`}
                checked={p.id === chosen}
                onChange={() => {
                  onChoose(p.id);
                  setPicking(false);
                }}
                className="h-4 w-4 accent-[#1b2d5e]"
              />
              <span className="min-w-0 flex-1 truncate font-semibold text-[#16213e]">{p.name}</span>
              <span className="flex-shrink-0 text-[12.5px] text-[#8189a0]">{fmtAgo(p.updatedAt)}</span>
            </label>
          ))}
          <button type="button" onClick={startNew} disabled={creating} className="inline-flex items-center gap-1.5 px-1 py-1 text-[13px] font-semibold text-[#1b2d5e]">
            <Plus className="h-4 w-4" /> {creating ? "Starting…" : "New project"}
          </button>
        </div>
      ) : null}

      {smallScreen && (
        <p className="mt-3 flex gap-2 rounded-xl bg-[#fff8ee] px-3 py-2 text-[13px] leading-5 text-[#7a4a10]">
          <Laptop className="mt-0.5 h-4 w-4 flex-shrink-0" />
          Scratch needs a bigger screen: a laptop, a computer, or a tablet held sideways.
        </p>
      )}

      <div className="mt-4">
        {current && !picking ? (
          <button
            type="button"
            onClick={() => onOpen(current.id)}
            className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-[#1b2d5e] px-6 py-3.5 text-[15px] font-bold text-white shadow-[0_8px_24px_rgba(27,45,94,0.25)] transition hover:bg-[#24397a] sm:w-auto"
          >
            <Icon className="h-5 w-5" /> {t.open}
          </button>
        ) : !isLoading && projects.length === 0 ? (
          <button
            type="button"
            onClick={startNew}
            disabled={creating}
            className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-[#1b2d5e] px-6 py-3.5 text-[15px] font-bold text-white shadow-[0_8px_24px_rgba(27,45,94,0.25)] transition hover:bg-[#24397a] disabled:opacity-50 sm:w-auto"
          >
            <Plus className="h-5 w-5" /> {creating ? "Starting…" : t.newProject}
          </button>
        ) : null}
      </div>
      {msg && <p className="mt-2 text-sm font-semibold text-[#b94a3a]">{msg}</p>}
      <p className="mt-3 text-[12.5px] leading-5 text-[#8189a0]">{t.elsewhere}</p>
      {kind === "scratch" && (
        <p className="mt-2 text-[11.5px] leading-4 text-[#a6adbf]">
          Scratch is a project of the Scratch Foundation, in collaboration with the MIT Media Lab (scratch.mit.edu). Future Smart Lab is not
          affiliated with them.
        </p>
      )}
    </div>
  );
}

/** On the Hand in step: hand the project in with the work (ticked when it was used for this lesson). */
export function ProjectAttach({
  childId,
  lessonKey,
  kind,
  projectId,
  setProjectId,
}: {
  childId: string;
  lessonKey: string;
  kind: WorkspaceKind;
  /** null = not handing a project in */
  projectId: string | null;
  setProjectId: (id: string | null) => void;
}) {
  const { data } = useGetProjectsQuery({ childId, kind });
  const projects = data?.data ?? [];
  const [lastPicked, setLastPicked] = useState<string | null>(projectId);
  useEffect(() => {
    if (projectId) setLastPicked(projectId);
  }, [projectId]);
  if (!projects.length) return null;
  const Icon = WORKSPACE_TEXT[kind].icon;
  const fallback = lastPicked ?? defaultProject(projects, lessonKey)?.id ?? null;

  return (
    <div className={`rounded-[20px] border-2 p-4 transition ${projectId ? "border-[#1b2d5e] bg-[#f6f8fc]" : "border-dashed border-[#c9cfe0] bg-white"}`}>
      <label className="flex cursor-pointer items-start gap-3">
        <input
          type="checkbox"
          checked={!!projectId}
          onChange={(e) => setProjectId(e.target.checked ? fallback : null)}
          className="mt-1 h-5 w-5 accent-[#1b2d5e]"
        />
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-2 text-[15px] font-bold text-[#16213e]">
            <Icon className="h-5 w-5 text-[#1b2d5e]" /> Hand in my {PROJECT_NOUN[kind]}
          </span>
          <span className="mt-0.5 block text-[13px] leading-5 text-[#5f6780]">
            Your tutor opens it and sees exactly what you made, as it is right now.
          </span>
        </span>
      </label>
      {projectId && projects.length > 1 && (
        <select
          value={projectId}
          onChange={(e) => setProjectId(e.target.value)}
          className="mt-3 w-full rounded-xl border border-[#dde2ec] bg-white px-3 py-2.5 text-[14px] text-[#16213e] outline-none focus:border-[#1b2d5e]"
          aria-label="Which project"
        >
          {projects.map((p) => (
            <option key={p.id} value={p.id}>{p.name} (saved {fmtAgo(p.updatedAt)})</option>
          ))}
        </select>
      )}
      {projectId && projects.length === 1 && <p className="mt-2 pl-8 text-[14px] font-semibold text-[#16213e]">{projects[0].name}</p>}
    </div>
  );
}
